'use client';

import { useState, useRef, useEffect } from 'react';
import styles from './GlobalCollapsibleButton.module.css';

interface GlobalCollapsibleButtonProps {
  title: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  className?: string;
  size?: 'default' | 'comfortable';
}

export default function GlobalCollapsibleButton({ 
  title,
  trailing,
  children, 
  isOpen = false, 
  onToggle,
  className = '',
  size = 'default',
}: GlobalCollapsibleButtonProps) {
  const [isExpanded, setIsExpanded] = useState(isOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [children]);

  const handleToggle = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    onToggle?.(newState);
  };

  const sizeClass = size === 'comfortable' ? styles.comfortable : '';

  return (
    <div className={`${styles.collapsibleContainer} ${sizeClass} ${className}`.trim()}>
      <button 
        className={styles.collapsibleButton}
        onClick={handleToggle}
        aria-expanded={isExpanded}
      >
        <span className={styles.buttonTitle}>{title}</span>
        <div className={styles.buttonTrailing}>
          {trailing ? <span className={styles.trailingBadge}>{trailing}</span> : null}
          <div className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path 
              d="M3 4.5L6 7.5L9 4.5" 
              stroke="currentColor" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
          </div>
        </div>
      </button>
      
      <div 
        className={`${styles.collapsibleContent} ${isExpanded ? styles.contentOpen : ''}`}
        style={{ height: isExpanded ? `${contentHeight}px` : '0px' }}
      >
        <div ref={contentRef} className={styles.contentInner}>
          {children}
        </div>
      </div>
    </div>
  );
}
